import { CreateNodeCommand } from '../../../core/commands/CreateNodeCommand';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import type { CompositeCommand } from '../../../core/commands/ICommand';
import type { Node } from '../../../core/models/scene';
import {
  GenogramLayoutService,
  resolveGenogramLayoutOptionsForFamily,
  type FamilyData,
} from '../../genogram/services/GenogramAILayoutingService';

type ViewportCenter = { x: number; y: number };

const buildFamilyDataForLayout = (
  nodes: Node[],
  edgeCommands: CreateEdgeCommand[]
): FamilyData | null => {
  if (!nodes.length) {
    return null;
  }

  const people: FamilyData['people'] = nodes.map(node => ({
    id: node.id,
    name: node.name ?? 'Person',
    gender: node.sex ?? 'unknown',
    culture: Array.isArray((node as any).culturalHeritageTags)
      ? (node as any).culturalHeritageTags.join(', ')
      : (node as any).culturalHeritageTags,
    sexualOrientation: (node as any).sexualOrientation,
    medicalConditions: (node as any).medicalConditionTags,
  }));

  const marriages: Array<[string, string]> = [];
  const marriageKeys = new Set<string>();
  const childParentIndex = new Map<string, Set<string>>();

  edgeCommands.forEach(edgeCommand => {
    const params = edgeCommand.getParams?.();
    if (!params || !params.from || !params.to) {
      return;
    }

    if (params.type === 'marriage') {
      const ordered = [params.from, params.to].sort();
      const key = ordered.join('::');
      if (!marriageKeys.has(key)) {
        marriageKeys.add(key);
        marriages.push([ordered[0], ordered[1]]);
      }
      return;
    }

    if (params.type === 'child') {
      const parents = childParentIndex.get(params.to) ?? new Set<string>();
      parents.add(params.from);
      childParentIndex.set(params.to, parents);
    }
  });

  const parentChildRelations = Array.from(childParentIndex.entries()).reduce<NonNullable<FamilyData['parentChild']>>((acc, [childId, parentsSet]) => {
    if (!parentsSet.size) {
      return acc;
    }
    const parents = Array.from(parentsSet).sort();
    const key = parents.join('::');
    const existing = acc.find(rel => rel.parents.join('::') === key);
    if (existing) {
      existing.children.push(childId);
      return acc;
    }

    acc.push({
      parents,
      children: [childId],
    });
    return acc;
  }, []);

  return {
    people,
    marriages: marriages.length ? marriages : undefined,
    parentChild: parentChildRelations.length ? parentChildRelations : undefined,
  };
};

export const applyLayoutToTemplate = (
  templateCommand: CompositeCommand,
  center: ViewportCenter,
  drawingWidth = 1800
): void => {
  const commands = templateCommand.getCommands?.() ?? [];
  const nodeCommands = commands.filter(
    (cmd): cmd is CreateNodeCommand => cmd instanceof CreateNodeCommand
  );
  if (!nodeCommands.length) {
    return;
  }

  const nodes = nodeCommands.map(cmd => cmd.getNode());
  const nodeMap = new Map(nodes.map(node => [node.id, node]));
  const edgeCommands = commands.filter(
    (cmd): cmd is CreateEdgeCommand => cmd instanceof CreateEdgeCommand
  );
  const familyData = buildFamilyDataForLayout(nodes, edgeCommands);
  if (!familyData) {
    return;
  }

  try {
    const layoutService = new GenogramLayoutService(
      resolveGenogramLayoutOptionsForFamily(familyData, drawingWidth)
    );
    const layoutNodes = layoutService.layout(familyData);
    if (!layoutNodes.length) {
      return;
    }

    const xs = layoutNodes.map(node => node.x);
    const ys = layoutNodes.map(node => node.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const offsetX = center.x - (minX + (maxX - minX) / 2);
    const offsetY = center.y - (minY + (maxY - minY) / 2);

    layoutNodes.forEach(layoutNode => {
      const node = nodeMap.get(layoutNode.id);
      if (!node) {
        return;
      }
      node.x = Math.round(layoutNode.x + offsetX);
      node.y = Math.round(layoutNode.y + offsetY);
    });
  } catch (error) {
    console.warn('[templateLayout] Failed to layout template', error);
  }
};
